import React, { useEffect, useState } from "react";
import { Link, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import {
  Mountain, Plane, CalendarClock, Dumbbell, User, Users2, LogOut, MapPin, ShieldCheck,
  CheckCircle2, CreditCard, Luggage, Cloud, Phone, FileText, Heart, Home,
} from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { PillButton, Overline } from "../components/primitives";
import { fmtDate, parseLocalDate } from "../lib/date";

function useCountdown(target) {
  const [d, setD] = useState(0);
  useEffect(() => {
    if (!target) return () => {};
    const t = () => setD(Math.max(0, Math.ceil((parseLocalDate(target) - new Date()) / 86400000)));
    t();
    const i = setInterval(t, 3600000);
    return () => {
      if (i) clearInterval(i);
    };
  }, [target]);
  return d;
}

const PACKING = ["Passport (valid 6+ months)", "Training clothes & shoes", "Swimwear", "Hiking shoes", "Sunscreen & sunglasses", "Reusable water bottle", "Light rain jacket", "Personal medications", "Recovery/mobility bands"];

function MyTrip() {
  const [bookings, setBookings] = useState([]);
  const [retreat, setRetreat] = useState(null);
  const [payments, setPayments] = useState([]);
  const [hotel, setHotel] = useState(null);

  useEffect(() => {
    api.get("/bookings/me").then((r) => { setBookings(r.data); if (r.data[0]) api.get(`/retreats/${r.data[0].retreat_id}`).then((rr) => setRetreat(rr.data)); }).catch(() => {});
    api.get("/payments/me").then((r) => setPayments(r.data)).catch(() => {});
    api.get("/hotels").then((r) => setHotel(r.data[0])).catch(() => {});
  }, []);

  const b = bookings[0];
  const days = useCountdown(b?.departure_start || retreat?.start_date);

  if (!b) return (
    <div className="rounded-3xl bg-white border border-black/10 p-10 text-center">
      <Plane className="w-10 h-10 text-primary mx-auto" />
      <h2 className="mt-4 font-display font-extrabold text-2xl">No trip booked yet</h2>
      <p className="mt-2 text-foreground/60">Ready for El Salvador? Reserve your spot on the 6-day retreat.</p>
      <Link to="/book" className="inline-block mt-6"><PillButton>Book Your Retreat</PillButton></Link>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Countdown hero */}
      <div className="relative rounded-3xl overflow-hidden">
        <img src={retreat?.hero_image} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 p-8 text-white">
          <Overline className="text-white/80">Your retreat</Overline>
          <div className="mt-2 font-display font-extrabold text-2xl md:text-3xl">{b.retreat_name}</div>
          <div className="mt-1 text-white/80 text-sm flex items-center gap-2"><CalendarClock className="w-4 h-4" /> {b.departure_label || b.retreat_name} · Departs {b.departure_start ? fmtDate(b.departure_start) : (retreat && fmtDate(retreat.start_date))}</div>
          <div className="mt-6 font-display font-extrabold text-6xl" data-testid="countdown">{days}<span className="text-2xl font-medium ml-2 text-white/70">days to go</span></div>
        </div>
      </div>

      {/* Payment status */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="rounded-2xl bg-white border border-black/10 p-6">
          <CreditCard className="w-5 h-5 text-primary" /><div className="overline mt-3 text-foreground/50">Paid</div>
          <div className="font-display font-extrabold text-2xl mt-1">${b.amount_paid?.toLocaleString()}</div>
        </div>
        <div className="rounded-2xl bg-white border border-black/10 p-6">
          <FileText className="w-5 h-5 text-primary" /><div className="overline mt-3 text-foreground/50">Balance remaining</div>
          <div className="font-display font-extrabold text-2xl mt-1">${b.amount_remaining?.toLocaleString()}</div>
        </div>
        <div className="rounded-2xl bg-white border border-black/10 p-6">
          <CheckCircle2 className="w-5 h-5 text-primary" /><div className="overline mt-3 text-foreground/50">Status</div>
          <div className="font-display font-extrabold text-xl mt-1 capitalize">{b.status?.replace(/_/g, " ")}</div>
        </div>
      </div>
      {b.amount_remaining > 0 && (
        <PayBalance booking={b} onPaid={() => window.location.reload()} />
      )}

      {/* Itinerary + info grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl bg-white border border-black/10 p-6">
          <h3 className="font-display font-bold text-lg flex items-center gap-2"><MapPin className="w-5 h-5 text-primary" /> Six-day itinerary</h3>
          <div className="mt-4 space-y-3">
            {retreat?.itinerary?.map((d) => (
              <div key={d.day} className="flex gap-3 text-sm">
                <span className="shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary grid place-items-center font-bold text-xs">D{d.day}</span>
                <div><div className="font-medium">{d.title}</div><div className="text-foreground/50 text-xs">{d.location}</div></div>
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-6">
          {hotel && (
            <div className="rounded-2xl bg-white border border-black/10 p-6">
              <h3 className="font-display font-bold text-lg flex items-center gap-2"><Home className="w-5 h-5 text-primary" /> Hotel</h3>
              <div className="mt-2 font-medium">{hotel.name}</div>
              <div className="text-sm text-foreground/60">{hotel.address}</div>
            </div>
          )}
          <div className="rounded-2xl bg-white border border-black/10 p-6">
            <h3 className="font-display font-bold text-lg flex items-center gap-2"><Plane className="w-5 h-5 text-primary" /> Flights & transport</h3>
            <p className="mt-2 text-sm text-foreground/60">Round-trip airfare & airport transfers are included. Your flight details and driver info will appear here once finalized by the team.</p>
          </div>
          <div className="rounded-2xl bg-white border border-black/10 p-6">
            <h3 className="font-display font-bold text-lg flex items-center gap-2"><Cloud className="w-5 h-5 text-primary" /> Weather</h3>
            <p className="mt-2 text-sm text-foreground/60">El Salvador coast: warm & tropical, ~82–90°F. Pack light, breathable clothing and sun protection.</p>
          </div>
        </div>
      </div>

      {/* Packing + emergency */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl bg-white border border-black/10 p-6">
          <h3 className="font-display font-bold text-lg flex items-center gap-2"><Luggage className="w-5 h-5 text-primary" /> Packing checklist</h3>
          <div className="mt-4 grid sm:grid-cols-2 gap-2">
            {PACKING.map((p, i) => <label key={i} className="flex items-center gap-2 text-sm"><input type="checkbox" className="w-4 h-4 accent-[#D95D39]" /> {p}</label>)}
          </div>
        </div>
        <div className="rounded-2xl bg-white border border-black/10 p-6">
          <h3 className="font-display font-bold text-lg flex items-center gap-2"><Phone className="w-5 h-5 text-primary" /> Emergency contacts</h3>
          <div className="mt-3 text-sm space-y-2">
            <div className="flex justify-between"><span className="text-foreground/60">Your contact</span><span>{b.traveler?.emergency_contact_name} · {b.traveler?.emergency_contact_phone}</span></div>
            <div className="flex justify-between"><span className="text-foreground/60">La Vida support</span><span>travelisvidainfo@gmail.com</span></div>
          </div>
          <div className="mt-4 rounded-xl bg-primary/5 border border-primary/20 p-3 text-xs text-foreground/70 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-primary" /> Travel documents & announcements will be posted here before departure.</div>
        </div>
      </div>
    </div>
  );
}

function PayBalance({ booking, onPaid }) {
  const [busy, setBusy] = useState(false);
  const pay = async () => {
    setBusy(true);
    try {
      const idem = `${booking.id}-${Date.now()}`;
      await api.post("/payments", { booking_id: booking.id, card_token: "tok_balance", idempotency_key: idem });
      toast.success("Payment received!"); onPaid();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl bg-primary/5 border border-primary/20 p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
      <div><div className="font-display font-bold">Balance due: ${booking.amount_remaining?.toLocaleString()}</div><div className="text-sm text-foreground/60">{booking.payment_plan === "installments" ? "Make your next installment payment." : "Pay your remaining balance anytime before departure."}</div></div>
      <PillButton onClick={pay} disabled={busy} data-testid="portal-pay-balance">{busy ? "Processing…" : "Make a payment"}</PillButton>
    </div>
  );
}

function FitnessPrep() {
  const [plans, setPlans] = useState([]);
  const [progress, setProgress] = useState([]);
  const [note, setNote] = useState("");
  useEffect(() => {
    api.get("/fitness/plans").then((r) => setPlans(r.data)).catch(() => {});
    api.get("/fitness/progress").then((r) => setProgress(r.data)).catch(() => {});
  }, []);
  const log = async () => {
    if (!note) return;
    const { data } = await api.post("/fitness/progress", { note });
    setProgress([data, ...progress]); setNote(""); toast.success("Progress logged!");
  };
  return (
    <div className="space-y-6">
      <div><Overline>Get ready to train</Overline><h2 className="mt-2 font-display font-extrabold text-2xl">Fitness prep</h2></div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {plans.map((p) => (
          <div key={p.id} className="rounded-2xl bg-white border border-black/10 p-6">
            <Dumbbell className="w-6 h-6 text-primary" />
            <div className="mt-3 font-display font-bold text-lg">{p.title}</div>
            <div className="text-xs text-primary">{p.weeks}-week plan</div>
            <p className="mt-2 text-sm text-foreground/60">{p.description}</p>
            <ul className="mt-3 space-y-1 text-sm text-foreground/70">{p.workouts.map((w, i) => <li key={i} className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary" /> {w}</li>)}</ul>
          </div>
        ))}
      </div>
      <div className="rounded-2xl bg-white border border-black/10 p-6">
        <h3 className="font-display font-bold text-lg">Progress tracker</h3>
        <div className="mt-3 flex gap-2">
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Ran 5k in 28min" data-testid="progress-input" className="flex-1 h-11 rounded-xl border border-black/10 px-4 focus:outline-none focus:ring-2 focus:ring-primary" />
          <PillButton onClick={log} data-testid="progress-log">Log</PillButton>
        </div>
        <div className="mt-4 space-y-2">
          {progress.map((p) => <div key={p.id} className="flex justify-between text-sm border-b border-black/5 py-2"><span>{p.note}</span><span className="text-foreground/40">{new Date(p.date).toLocaleDateString()}</span></div>)}
          {progress.length === 0 && <div className="text-sm text-foreground/50">No entries yet. Start logging your training!</div>}
        </div>
      </div>
    </div>
  );
}

function Profile() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (user) setForm(user); }, [user]);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/profile", {
        phone: form.phone, address: form.address, passport_number: form.passport_number, passport_expiry: form.passport_expiry,
        emergency_contact_name: form.emergency_contact_name, emergency_contact_phone: form.emergency_contact_phone,
        dietary_restrictions: form.dietary_restrictions, fitness_level: form.fitness_level, medical_info: form.medical_info,
      });
      await refreshUser(); toast.success("Profile updated!");
    } catch { toast.error("Could not save."); } finally { setBusy(false); }
  };
  const cls = "w-full h-11 rounded-xl border border-black/10 px-4 focus:outline-none focus:ring-2 focus:ring-primary";
  const set = (k, v) => setForm({ ...form, [k]: v });
  return (
    <div className="space-y-6 max-w-2xl">
      <div><Overline>Your details</Overline><h2 className="mt-2 font-display font-extrabold text-2xl">Profile</h2></div>
      <div className="rounded-2xl bg-white border border-black/10 p-6 grid sm:grid-cols-2 gap-4">
        <div><label className="text-sm font-medium">Phone</label><input className={cls} value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} /></div>
        <div><label className="text-sm font-medium">Address</label><input className={cls} value={form.address || ""} onChange={(e) => set("address", e.target.value)} /></div>
        <div><label className="text-sm font-medium">Passport #</label><input className={cls} value={form.passport_number || ""} onChange={(e) => set("passport_number", e.target.value)} /></div>
        <div><label className="text-sm font-medium">Passport expiry</label><input type="date" className={cls} value={form.passport_expiry || ""} onChange={(e) => set("passport_expiry", e.target.value)} /></div>
        <div><label className="text-sm font-medium">Emergency contact</label><input className={cls} value={form.emergency_contact_name || ""} onChange={(e) => set("emergency_contact_name", e.target.value)} /></div>
        <div><label className="text-sm font-medium">Emergency phone</label><input className={cls} value={form.emergency_contact_phone || ""} onChange={(e) => set("emergency_contact_phone", e.target.value)} /></div>
        <div><label className="text-sm font-medium">Dietary restrictions</label><input className={cls} value={form.dietary_restrictions || ""} onChange={(e) => set("dietary_restrictions", e.target.value)} /></div>
        <div><label className="text-sm font-medium">Fitness level</label>
          <select className={cls} value={form.fitness_level || "Intermediate"} onChange={(e) => set("fitness_level", e.target.value)}>{["Beginner", "Intermediate", "Advanced", "Athlete"].map((f) => <option key={f}>{f}</option>)}</select>
        </div>
        <div className="sm:col-span-2"><label className="text-sm font-medium">Medical info</label><textarea rows={3} className="w-full rounded-xl border border-black/10 p-4 focus:outline-none focus:ring-2 focus:ring-primary" value={form.medical_info || ""} onChange={(e) => set("medical_info", e.target.value)} /></div>
      </div>
      <PillButton onClick={save} disabled={busy} data-testid="profile-save">{busy ? "Saving…" : "Save changes"}</PillButton>
    </div>
  );
}

function Community() {
  const [posts, setPosts] = useState([]);
  const [content, setContent] = useState("");
  useEffect(() => { api.get("/community/posts").then((r) => setPosts(r.data)).catch(() => {}); }, []);
  const post = async () => {
    if (!content) return;
    const { data } = await api.post("/community/posts", { content });
    setPosts([data, ...posts]); setContent(""); toast.success("Posted!");
  };
  const like = async (id) => {
    const { data } = await api.post(`/community/posts/${id}/like`);
    setPosts(posts.map((p) => p.id === id ? { ...p, liked: data.liked, like_count: p.like_count + (data.liked ? 1 : -1) } : p));
  };
  return (
    <div className="space-y-6 max-w-2xl">
      <div><Overline>Trip memories & challenges</Overline><h2 className="mt-2 font-display font-extrabold text-2xl">Community</h2></div>
      <div className="rounded-2xl bg-white border border-black/10 p-5">
        <textarea rows={3} value={content} onChange={(e) => setContent(e.target.value)} placeholder="Share a fitness win or trip memory…" data-testid="post-input" className="w-full rounded-xl border border-black/10 p-4 focus:outline-none focus:ring-2 focus:ring-primary" />
        <div className="mt-3 flex justify-end"><PillButton onClick={post} data-testid="post-submit">Post</PillButton></div>
      </div>
      {posts.map((p) => (
        <div key={p.id} className="rounded-2xl bg-white border border-black/10 p-5">
          <div className="font-display font-bold">{p.author}</div>
          <div className="text-xs text-foreground/40">{new Date(p.created_at).toLocaleString()}</div>
          <p className="mt-3 text-foreground/80">{p.content}</p>
          <button onClick={() => like(p.id)} data-testid={`like-${p.id}`} className={`mt-3 inline-flex items-center gap-1.5 text-sm ${p.liked ? "text-primary" : "text-foreground/50"} hover:text-primary`}>
            <Heart className={`w-4 h-4 ${p.liked ? "fill-current" : ""}`} /> {p.like_count}
          </button>
        </div>
      ))}
    </div>
  );
}

const nav = [
  { to: "/portal", label: "My Trip", icon: Plane, end: true },
  { to: "/portal/fitness", label: "Fitness Prep", icon: Dumbbell },
  { to: "/portal/profile", label: "Profile", icon: User },
  { to: "/portal/community", label: "Community", icon: Users2 },
];

export default function Portal() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-secondary/30">
      <div className="glass-nav bg-white/80 border-b border-black/5 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 font-display font-extrabold"><Mountain className="w-5 h-5 text-primary" /> La Vida Is Travel Fit</button>
          <div className="flex items-center gap-4">
            <span className="text-sm text-foreground/60 hidden sm:inline">Hi, {user?.name?.split(" ")[0]}</span>
            <button onClick={logout} className="text-foreground/60 hover:text-primary" data-testid="portal-logout"><LogOut className="w-5 h-5" /></button>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 py-8 grid lg:grid-cols-[220px_1fr] gap-8">
        <aside className="lg:sticky lg:top-24 h-max">
          <nav className="flex lg:flex-col gap-1 overflow-x-auto">
            {nav.map((n) => {
              const active = n.end ? location.pathname === n.to : location.pathname.startsWith(n.to);
              return (
                <Link key={n.to} to={n.to} data-testid={`portal-nav-${n.label.toLowerCase().replace(" ", "-")}`}
                  className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${active ? "bg-primary text-white" : "hover:bg-white text-foreground/70"}`}>
                  <n.icon className="w-4 h-4" /> {n.label}
                </Link>
              );
            })}
          </nav>
        </aside>
        <div>
          <Routes>
            <Route index element={<MyTrip />} />
            <Route path="fitness" element={<FitnessPrep />} />
            <Route path="profile" element={<Profile />} />
            <Route path="community" element={<Community />} />
            <Route path="*" element={<Navigate to="/portal" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}
